/**
 * Hook `usePermissions` — mirror del desktop
 * (`apps/desktop/src/renderer/src/lib/permissions.ts`).
 *
 * Evalúa permisos del rol actual contra la matriz efectiva del tenant
 * (default ∪ overrides). Solo controla UI: el worker valida cada
 * mutación con `assertPermission`, así que un cliente rebelde que
 * ignore el hook sigue topando con FORBIDDEN.
 */

import {
  DEFAULT_PERMISSION_MATRIX,
  type PermissionId,
  type UserRole,
} from '@atreus/core';

import { loadSession } from '../auth/session.js';

import { trpc } from './trpc.js';

export interface UsePermissionsResult {
  canDo: (id: PermissionId) => boolean;
  isLoaded: boolean;
  role: UserRole | null;
}

export const usePermissions = (): UsePermissionsResult => {
  const matrix = trpc.permissions.matrix.useQuery(undefined, {
    staleTime: 30_000,
  });

  const session = loadSession();
  // En pos-web solo entran roles de tenant (admin/manager/cashier/waiter) —
  // el Login ya bloquea superadmin. Si por algún motivo la sesión se perdió,
  // devolvemos null y todo `canDo` da false (fail-closed).
  const role: UserRole | null = session?.user.role ?? null;

  return {
    canDo: (id) => {
      if (!role) return false;
      const row = (matrix.data ?? []).find(
        (r) => r.role === role && r.permissionId === id,
      );
      if (row) return row.granted;
      // Fallback mientras matrix carga: usamos el default del rol para
      // evitar flicker de botones ocultos. El backend sigue siendo la
      // autoridad — esto solo pule la UX.
      return DEFAULT_PERMISSION_MATRIX[role]?.has(id) ?? false;
    },
    isLoaded: !matrix.isLoading,
    role,
  };
};
